import React, { useState, useEffect } from 'react';
import UploadReceiptModal from './UploadReceiptModal';
import { supabase } from '../../lib/supabase';

interface UploadReceiptModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  /** Pre-load an attachment from a URL (e.g. email attachment) */
  initialFileUrl?: string;
  initialFileName?: string;
  initialContentType?: string;
  /** When provided AND the user is on mobile, the upload receipt modal opens in camera view with a "Manual" escape. */
  onSwitchToManual?: (
    preselection?: { bookingId?: string | null; projectId?: string | null }
  ) => void;
}

export default function UploadReceiptModalWrapper({
  isOpen,
  onClose,
  businessId,
  initialFileUrl,
  initialFileName,
  initialContentType,
  onSwitchToManual
}: UploadReceiptModalWrapperProps) {
  const [categories, setCategories] = useState<any[]>([]);
  const [initialFile, setInitialFile] = useState<File | null>(null);
  const [initialPreviewUrl, setInitialPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && businessId) {
      fetchCategories();
    }
  }, [isOpen, businessId]);

  useEffect(() => {
    if (!isOpen || !initialFileUrl) {
      setInitialFile(null);
      setInitialPreviewUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(initialFileUrl);
        const blob = await res.blob();
        if (cancelled) return;
        const type = initialContentType || blob.type || 'application/octet-stream';
        const name = initialFileName || 'attachment';
        const file = new File([blob], name, { type });
        setInitialFile(file);
        if (type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => { if (!cancelled) setInitialPreviewUrl(reader.result as string); };
          reader.readAsDataURL(file);
        } else {
          setInitialPreviewUrl(name);
        }
      } catch (err) {
        console.error('Failed to fetch attachment for expense:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, initialFileUrl, initialFileName, initialContentType]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('order_index');

      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }

      setCategories(data || []);

      if (!data || data.length === 0) {
        const { error: createError } = await supabase.rpc('create_default_expense_categories', {
          p_business_id: businessId
        });

        if (!createError) {
          const { data: newCategories } = await supabase
            .from('expense_categories')
            .select('*')
            .eq('business_id', businessId)
            .eq('is_active', true)
            .order('order_index');

          setCategories(newCategories || []);
        }
      }
    } catch (error) {
      console.error('Error in fetchCategories:', error);
    }
  };

  return (
    <UploadReceiptModal
      isOpen={isOpen}
      onClose={onClose}
      onSave={() => {
        onClose();
        window.dispatchEvent(new CustomEvent('expensesUpdated'));
      }}
      businessId={businessId}
      categories={categories}
      onCategoriesChange={fetchCategories}
      initialFile={initialFile}
      initialPreviewUrl={initialPreviewUrl}
      onSwitchToManual={onSwitchToManual}
    />
  );
}

